import React, { FC, useEffect, useState } from "react";
import {
  SplitDropDown,
  NavPills,
  DashboardTopBar,
  ItemContentArea,
  UpsertInvoicePopUp,
  RelatedOrdersGrid,
  RelatedSubscriptionGrid,
  RelatedPaymentsGrid,
  RelatedPaymentMethodGrid,
} from "../../../shared/components";
import { observer } from "mobx-react";
import { NavigateFunction, Params } from "react-router-dom";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import DeleteInvoicePopUp from "./DeleteInvoicePopUp";
import CancelInvoicePopUp from "./CancelInvoicePopUp";
import CancelIcon from "../../../../../../images/products/cross-blue.svg";
import PayIcon from "../../../../../../images/placeholders/pay-blue.svg";
import { firstValueFrom } from "rxjs";
import DetailSection from "./DetailSection";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  NavigationButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  InvoiceStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { InvoiceService } from "../../../shared/services";
import {
  InvoiceDetailPageModel,
  SelectedItemModel,
  UpsertInvoiceModel,
} from "../../../shared/models";
import { ItemDetailMainWrapper, MainOuterCard } from "../../../shared/styles";
import { NewPaymentRelatedSource } from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";

type IProps = {
  invoiceStore: InvoiceStore;
  invoiceId: string;
  params: Readonly<Params<string>>;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

type OptionValues = {
  icon: string;
  label: string;
  value: string;
  onClick: () => void;
};
const InvoiceDetailMain: FC<IProps> = ({
  params,
  invoiceStore,
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showInvoicePopUp, setShowInvoicePopUp] = React.useState(false);
  const [showCancelPopUp, setShowCancelPopUp] = React.useState(false);
  const [showDeletePopUp, setShowDeletePopUp] = React.useState(false);
  const [showPayPopUp, setShowPayPopUp] = React.useState(false);
  const [activeNavPill, setActiveNavPill] = useState("details");
  const [showLoader, setShowLoader] = useState(false);

  function invoiceId() {
    const id = params.id || "";
    return UtilityMethods.isValidObjectId(id) ? id : "";
  }

  useEffect(() => {
    fetchData();
    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [invoiceId()]);

  const fetchData = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      InvoiceService.getInvoice(
        AuthService.authData.accountId,
        invoiceId(),
        invoiceStore.searchValue
      )
    );
    const details = InvoiceDetailPageModel.deserializeAPIModel(response.result);
    invoiceStore.updateDetailPage(details);
    setShowLoader(false);
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/invoices",
        title: details.detail.invoiceNumber,
        icon: "",
      })
    );
  };

  function togglePopUp(show: boolean) {
    setShowInvoicePopUp(show);
  }

  if (invoiceStore.detailPage === null) return null;

  const navPills = () => {
    return (
      <FlexContainer alignItems="center" padding="0px 0px 30px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Invoice
        </CustomLabel>
        <div className="flex-auto-grow">
          {false && (
            <NavPills
              defaultActiveKey="details"
              pills={[
                {
                  key: "details",
                  title: "DETAILS",
                },
                {
                  key: "activity",
                  title: "ACTIVITY",
                },
              ]}
              onSelect={(key) => setActiveNavPill(key)}
            />
          )}
        </div>
      </FlexContainer>
    );
  };

  function renderDetails(node: React.ReactNode) {
    if (!invoiceStore.detailPage) return null;
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={
            <>
              <NavigationButtons
                currentIndex={invoiceStore.detailPage.index}
                totalItems={invoiceStore.detailPage.totalInvoices}
                previousRecordId={invoiceStore.detailPage.previousRecordId}
                nextRecordId={invoiceStore.detailPage.nextRecordId}
                onNavigate={(id) => navigate(`/app/invoices/detail/${id}`)}
              />
              {actionDropdown()}
            </>
          }
          rolesAndPermissionStore={rolesAndPermissionStore}
          merchantAccountStore={merchantAccountStore}
        />
        <ItemContentArea
          node={node}
          itemStore={itemStore}
          showLoader={showLoader}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function actionDropdown() {
    const options: OptionValues[] = [];

    if (invoiceStore.detailPage?.detail?.status !== "Paid") {
      options.push({
        icon: EditIcon,
        label: "Edit",
        value: "edit",
        onClick: () => {
          if (!invoiceId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
          togglePopUp(true);
        },
      });
    }

    if (
      invoiceStore.detailPage?.detail?.status !== "Void" &&
      invoiceStore.detailPage?.detail?.status !== "Paid"
    ) {
      options.push({
        icon: CancelIcon,
        label: "Cancel",
        value: "cancel",
        onClick: () => {
          if (!invoiceId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Cancel")) return;
          setShowCancelPopUp(true);
        },
      });
    }

    if (invoiceStore.detailPage?.detail?.status === "Draft") {
      options.push({
        icon: DeleteIcon,
        label: "Delete",
        value: "delete",
        onClick: () => {
          if (!invoiceId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Delete")) return;
          setShowDeletePopUp(true);
        },
      });
    }

    if (invoiceStore.detailPage?.detail?.isPayPopUpAllowed) {
      options.push({
        icon: PayIcon,
        label: "Pay",
        value: "pay",
        onClick: () => {
          if (!invoiceId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Pay")) return;
          setShowPayPopUp(true);
        },
      });
    }

    if (options.length === 0) return null;
    return (
      <SplitDropDown
        label={options[0].label}
        onClick={() => {
          if (!invoiceId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
          options[0]?.onClick();
        }}
        className="edit-dropdown-item"
        options={options}
      />
    );
  }

  function closeDeletePopUp(deleted: boolean) {
    setShowDeletePopUp(false);
    if (deleted) {
      navigate("/app/invoices");
    }
  }

  function closeUpdatePopUp(refresh: boolean) {
    if (refresh) {
      fetchData();
    }

    setShowInvoicePopUp(false);
  }

  function closeCancelPopUp(refresh: boolean) {
    if (refresh) {
      fetchData();
    }

    setShowCancelPopUp(false);
  }

  function closePayPopUP(refresh: boolean) {
    if (refresh) {
      fetchData();
    }

    setShowPayPopUp(false);
  }
  return renderDetails(
    <ItemDetailMainWrapper>
      {showCancelPopUp && (
        <CancelInvoicePopUp
          invoiceNumber={invoiceStore.detailPage.detail.invoiceNumber}
          status={invoiceStore.detailPage.detail.status}
          id={invoiceStore.detailPage.detail.id}
          contact={invoiceStore.detailPage.detail.contact}
          balance={invoiceStore.detailPage.detail.balance}
          dueDate={invoiceStore.detailPage.detail.dueDate}
          onClose={(refresh) => closeCancelPopUp(refresh)}
        />
      )}
      {showDeletePopUp && (
        <DeleteInvoicePopUp
          invoiceNumber={invoiceStore.detailPage.detail.invoiceNumber}
          status={invoiceStore.detailPage.detail.status}
          id={invoiceStore.detailPage.detail.id}
          contact={invoiceStore.detailPage.detail.contact}
          balance={invoiceStore.detailPage.detail.balance}
          dueDate={invoiceStore.detailPage.detail.dueDate}
          onClose={(deleted) => closeDeletePopUp(deleted)}
        />
      )}
      {showInvoicePopUp && invoiceStore?.detailPage?.detail && (
        <UpsertInvoicePopUp
          invoiceData={
            new UpsertInvoiceModel(
              UpsertInvoiceModel.createModelFromInvoiceDetail(
                invoiceStore?.detailPage?.detail,
                invoiceStore?.detailPage?.payments
              )
            )
          }
          onClose={(refresh: boolean) => closeUpdatePopUp(refresh)}
          contact={invoiceStore.detailPage.detail?.contact}
          countriesWithState ={itemStore.countryStore.countriesWithState}
        />
      )}

      {showPayPopUp && invoiceStore?.detailPage?.detail && (
        <UpsertInvoicePopUp
          invoiceData={
            new UpsertInvoiceModel({
              ...UpsertInvoiceModel.createModelFromInvoiceDetail(
                invoiceStore?.detailPage?.detail,
                invoiceStore?.detailPage?.payments
              ),
              isPayPopUp: true,
            })
          }
          countriesWithState ={itemStore.countryStore.countriesWithState}
          onClose={(refresh: boolean) => closePayPopUP(refresh)}
          contact={invoiceStore.detailPage.detail?.contact}
        />
      )}
      <MainOuterCard>
        {navPills()}
        <CustomLabel
          color="#2C3E50"
          fontSize={"20px"}
          fontWeight={"600"}
          margin="5px 0px 25px"
        >
          Sold To
        </CustomLabel>
        {activeNavPill === "details" && (
          <DetailSection
            data={invoiceStore.detailPage.detail}
            lastPayment={invoiceStore.detailPage.lastPayment}
          />
        )}
      </MainOuterCard>
      {activeNavPill === "details" && (
        <>
          <RelatedOrdersGrid orders={invoiceStore.detailPage.orders} 
          countriesWithState={itemStore.countryStore.countriesWithState} />
          <RelatedSubscriptionGrid
            subscriptions={invoiceStore.detailPage.subscriptions}
            countriesWithState={itemStore.countryStore.countriesWithState}
            onUpdate={() => fetchData()}
            selectedContact={invoiceStore.detailPage.detail.contact}
            rolesAndPermissionStore={rolesAndPermissionStore}
          />
          <RelatedPaymentsGrid
            payments={invoiceStore.detailPage.payments}
            countriesWithState={itemStore.countryStore.countriesWithState}
            onUpdate={() => fetchData()}
            contact={invoiceStore.detailPage.detail.contact}
            source={NewPaymentRelatedSource.Invoice}
            sourceId={invoiceStore.detailPage.detail.id}
            rolesAndPermissionStore={rolesAndPermissionStore}
          />
          <RelatedPaymentMethodGrid
            paymentMethods={invoiceStore.detailPage.paymentMethods}
            onUpdate={() => fetchData()}
            contact={invoiceStore.detailPage.detail.contact}
            source={NewPaymentRelatedSource.Invoice}
            sourceId={invoiceStore.detailPage.detail.id}
            rolesAndPermissionStore={rolesAndPermissionStore}
          />
        </>
      )}
    </ItemDetailMainWrapper>
  );
};
export default withRouter(observer(InvoiceDetailMain));
